import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { FileText } from "lucide-react";

import { getButtonClassName } from "components/UserCardList/helpers/userCard";

import { JOB_APPLICATION_MATCH_STATUS } from "lookup";
import { ModalContext } from "context/providers";

import ActionButton from "./ActionButton";
import NumericCircle from "components/NumericCircle";
import { CTA_TITLES } from "components/UserCardList/constants/ctaTitles";
import { getUserName } from "utils/helpers/users";
import { isMatchPreShortlistedStatusType } from "utils/helpers/match";
import { cn } from "utils";
import SvgIcon from "components/base/SvgIcon";

const DEFAULT_BUTTON_CLASS_NAME =
  "transition-all whitespace-nowrap text-sm font-bold py-2 px-3 rounded-lg inline-flex items-center";

const HitActionButtons = ({
  hit,
  isLoading,
  showActions,
  matchAction,
  jobType,
  isJobStatusActive,
  hasPrimaryMatcher,
}) => {
  const { showModal } = useContext(ModalContext);

  const oneOfButtonsLoading = useMemo(() => {
    return !!Object.values(isLoading).filter((el) => !!el).length;
  }, [isLoading]);

  const matchStatus = hit.associatedMatch?.status;

  const canBeMatched =
    matchStatus &&
    (isMatchPreShortlistedStatusType(matchStatus) ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.INTERESTED);

  const canBeSkipped =
    matchStatus &&
    (isMatchPreShortlistedStatusType(matchStatus) ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED);

  const matchButtonTitle = !isJobStatusActive
    ? `${CTA_TITLES.match} This job is not active.`
    : !hasPrimaryMatcher
    ? `${CTA_TITLES.match} This job has no primary matcher.`
    : hit.hasActivePlacements
    ? `${CTA_TITLES.match} WARNING: User has active engagement(s).`
    : CTA_TITLES.match;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4">
        {showActions && (
          <>
            {(!hit.associatedMatch || canBeMatched) && (
              <ActionButton
                isDisabled={
                  oneOfButtonsLoading ||
                  !isJobStatusActive ||
                  !hasPrimaryMatcher
                }
                isLoading={isLoading[JOB_APPLICATION_MATCH_STATUS.MATCHED]}
                action={() => {
                  if (!hit.hasActivePlacements) {
                    matchAction(
                      {
                        status: JOB_APPLICATION_MATCH_STATUS.MATCHED,
                      },
                      !!matchStatus
                    );
                  } else if (
                    window.confirm(
                      "Are you sure you want to match this user, even though they have active engagement(s) currently?"
                    )
                  )
                    matchAction(
                      {
                        status: JOB_APPLICATION_MATCH_STATUS.MATCHED,
                      },
                      !!matchStatus
                    );
                }}
                title={matchButtonTitle}
                className="text-gray-600"
                type="match"
                iconType={hit.hasActivePlacements ? "warning" : "heart"}
              />
            )}

            {!hit.associatedMatch && (
              <button
                className={cn(
                  DEFAULT_BUTTON_CLASS_NAME,
                  "h-9 w-9 justify-center px-0 text-gray-600",
                  getButtonClassName(
                    "shortlist",
                    isLoading[JOB_APPLICATION_MATCH_STATUS.SHORTLISTED]
                  )
                )}
                disabled={oneOfButtonsLoading}
                onClick={() =>
                  matchAction(
                    {
                      status: JOB_APPLICATION_MATCH_STATUS.SHORTLISTED,
                    },
                    !!matchStatus
                  )
                }
                title={CTA_TITLES.shortlist}
                aria-label={CTA_TITLES.shortlist}
              >
                <SvgIcon type="focus" className="w-5 h-5" />
              </button>
            )}

            {(!hit.associatedMatch || canBeSkipped) && (
              <button
                className={cn(
                  DEFAULT_BUTTON_CLASS_NAME,
                  "h-9 w-9 justify-center px-0 text-gray-600",
                  getButtonClassName(
                    "skip",
                    isLoading[JOB_APPLICATION_MATCH_STATUS.SKIPPED]
                  )
                )}
                disabled={oneOfButtonsLoading}
                onClick={() =>
                  matchAction(
                    { status: JOB_APPLICATION_MATCH_STATUS.SKIPPED },
                    !!matchStatus
                  )
                }
                title={CTA_TITLES.skip}
                aria-label={CTA_TITLES.skip}
              >
                <SvgIcon type="skip" className="w-5 h-5" />
              </button>
            )}
          </>
        )}

        <button
          className={cn(
            DEFAULT_BUTTON_CLASS_NAME,
            "text-gray-600",
            getButtonClassName("note")
          )}
          title={CTA_TITLES.userNotes}
          onClick={() =>
            showModal({
              type: "note",
              title: `User notes: ${getUserName(hit)}`,
              userId: hit.id,
            })
          }
        >
          <FileText className="h-[15px] w-[15px]" />

          <p className="ml-2">
            Notes {!!hit.notesCount && <NumericCircle value={hit.notesCount} />}
          </p>
        </button>
      </div>
    </div>
  );
};

HitActionButtons.propTypes = {
  hit: PropTypes.object,
  isLoading: PropTypes.object,
  showActions: PropTypes.bool,
  matchAction: PropTypes.func,
};

HitActionButtons.defaultProps = {
  hit: {},
  showActions: false,
  isLoading: {
    [JOB_APPLICATION_MATCH_STATUS.MATCHED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SHORTLISTED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SKIPPED]: false,
  },
  matchAction: () => {},
};

export default HitActionButtons;
